import RankingStatsAggregator from '../calculators/RankingStatsAggregator.js';
import BattingRankingStats from '../enums/BattingRankingStats.js';
import PitchingRankingStats from '../enums/PitchingRankingStats.js';
import RankingScope from '../enums/RankingScope.js';
import RankingScopeContext from '../enums/RankingScopeContext.js';
import RankingScopesFilter from '../filters/RankingScopesFilter.js';

/**
 * Based on:
 * - https://en.wikipedia.org/wiki/Box_score_(baseball)
 * - https://www.mlb.com/gameday/padres-vs-reds/2021/03/14/642156#game_state=final,game_tab=box,game=642156
 * - https://www.mlb.com/gameday/giants-vs-d-backs/2021/03/14/642212#game_state=final,game_tab=box,game=642212
 * - https://www.mlb.com/gameday/cardinals-vs-mets/2021/03/14/641993#game_state=final,game_tab=box,game=641993
 */
class BoxScoreSummaryProvider {
    constructor(rankingStatsAggregator, rankingScopesFilter) {
        if (typeof(rankingStatsAggregator) === 'undefined') {
            rankingStatsAggregator = new RankingStatsAggregator();
        }
        else if (!(rankingStatsAggregator instanceof RankingStatsAggregator)) {
            throw new TypeError('rankingStatsAggregator must be an instance inheriting from RankingStatsAggregator class or undefined to use default implementation');
        }

        this.rankingStatsAggregator = rankingStatsAggregator;

        if (typeof(rankingScopesFilter) === 'undefined') {
            rankingScopesFilter = new RankingScopesFilter();
        }
        else if (!(rankingScopesFilter instanceof RankingScopesFilter)) {
            throw new TypeError('rankingScopesFilter must be an instance inheriting from RankingScopesFilter class or undefined to use default implementation');
        }

        this.rankingScopesFilter = rankingScopesFilter;
    }

    getBattingSummary(rankingStats, teamId) {
        this.verifyInput(rankingStats, teamId, true);

        const summary = {
            [BattingRankingStats.TwoBaseHits.key]: [],
            [BattingRankingStats.ThreeBaseHits.key]: [],
            [BattingRankingStats.HomeRuns.key]: [],
            [BattingRankingStats.TotalBases.key]: [],
            [BattingRankingStats.RunsBattedIn.key]: [],
            [BattingRankingStats.SacrificeFlies.key]: [],
            [BattingRankingStats.SacrificeHits.key]: [],
            [BattingRankingStats.GroundIntoDoublePlays.key]: [],
            // TODO, not exactly sure about what it means on MLB
            // Runners left in scoring position, 2 out:
            // Team RISP
            // team LOB
        };
        // defines parameters which will be calculated for batter/pitcher matchup
        const matchupParams = [
            BattingRankingStats.TwoBaseHits.key, BattingRankingStats.ThreeBaseHits.key,
            BattingRankingStats.HomeRuns.key,
        ];
        // defines parameters which will be calculated for batter only
        const batterParams = [
            BattingRankingStats.RunsBattedIn.key, BattingRankingStats.SacrificeFlies.key,
            BattingRankingStats.SacrificeHits.key, BattingRankingStats.GroundIntoDoublePlays.key,
            BattingRankingStats.TotalBases.key,
        ];

        const rankingParams = Object.keys(summary);
        const filterBy = { context: RankingScopeContext.Batting.key };
        if (teamId) {
            filterBy.teamId = teamId;
        }
        const statsFiltered = this.rankingScopesFilter.filter(
            rankingStats, filterBy
        );
        const statsPerMatchup = this.rankingStatsAggregator.aggregate(
            statsFiltered, rankingParams, [RankingScope.tagId.key, RankingScope.opponentId.key]
        );

        for (const playerStats of statsPerMatchup) {
            this.fillStatsSummary(summary, matchupParams, playerStats);
        }

        const statsPerBatter = this.rankingStatsAggregator.aggregate(
            statsFiltered, rankingParams, [RankingScope.tagId.key]
        );
        for (const playerStats of statsPerBatter) {
            this.fillStatsSummary(summary, batterParams, playerStats);
        }

        return summary;
    }

    getBaseRunningSummary(rankingStats, teamId) {
        this.verifyInput(rankingStats, teamId, true);

        const summary = {
            [BattingRankingStats.StolenBases.key]: [],
            [BattingRankingStats.CaughtStealing.key]: [],
            // TODO, not exactly sure about what it means on MLB
            // PO ?
        };

        const rankingParams = Object.keys(summary);
        const filterBy = { context: RankingScopeContext.Batting.key };
        if (teamId) {
            filterBy.teamId = teamId;
        }
        const statsFiltered = this.rankingScopesFilter.filter(
            rankingStats, filterBy
        );
        const statsPerRunner = this.rankingStatsAggregator.aggregate(
            statsFiltered, rankingParams, [RankingScope.tagId.key] // , RankingScope.opponentId.key would cause it per matchup
        );

        for (const playerStats of statsPerRunner) {
            this.fillStatsSummary(summary, rankingParams, playerStats);
        }

        return summary;
    }

    getFieldingSummary(rankingStats, teamId) {
        this.verifyInput(rankingStats, teamId, true);

        return {};
        // TODO - not calculated yet, needs to be added to the ranking stats
        // Pickoffs,
        // DB - double plays
        // TP - triple plays
        // PB? https://www.mlb.com/gameday/royals-vs-dodgers/2021/03/14/642000#game_state=final,game_tab=box,game=642000
        // E - errors
        // outfield assists: https://www.mlb.com/gameday/d-backs-vs-mariners/2021/03/15/641830#game_state=final,game_tab=box,game=641830
    }

    getPitchingSummary(rankingStats, teamId) {
        this.verifyInput(rankingStats, teamId, true);

        const summary = {
            [PitchingRankingStats.BattersFaced.key]: [],
            [PitchingRankingStats.HitBatsmen.key]: [], // @see https://www.mlb.com/gameday/padres-vs-reds/2021/03/14/642156#game_state=final,game_tab=box,game=642156
            [PitchingRankingStats.WildPitches.key]: [],
            // TODO -add more params later 
            // pitches-strikes
            // groundouts-flyouts
            // inherited runners-scored
        };

        // defines parameters which will be calculated for pitcher / batter matchup
        const matchupParams = [PitchingRankingStats.HitBatsmen.key];
        // defines parameters for pitcher only
        const pitcherParams = [PitchingRankingStats.BattersFaced.key, PitchingRankingStats.WildPitches.key];

        const rankingParams = Object.keys(summary);
        const filterBy = { context: RankingScopeContext.Pitching.key };
        if (teamId) {
            filterBy.teamId = teamId;
        }
        const statsFiltered = this.rankingScopesFilter.filter(
            rankingStats, filterBy
        );
        const statsPerMatchup = this.rankingStatsAggregator.aggregate(
            statsFiltered, rankingParams, [RankingScope.tagId.key, RankingScope.opponentId.key]
        );

        for (const playerStats of statsPerMatchup) {
            this.fillStatsSummary(summary, matchupParams, playerStats);
        }

        const statsPerPitcher = this.rankingStatsAggregator.aggregate(
            statsFiltered, rankingParams, [RankingScope.tagId.key]
        );

        for (const playerStats of statsPerPitcher) {
            this.fillStatsSummary(summary, pitcherParams, playerStats);
        }

        return summary;
    }

    verifyInput(rankingStats, teamId, isTeamIdOptional) {
        if (!(rankingStats instanceof Array)) {
            throw new TypeError('rankingStats must be an array');
        }

        if (!isTeamIdOptional && typeof(teamId) === 'undefined') {
            throw new TypeError('teamId must be an integer');
        }

        if (typeof(teamId) !== 'undefined' && (!Number.isInteger(teamId) || teamId < 1)) {
            // verify type...
            throw new TypeError('teamId must be an integer greater than 0');
        }
    }

    fillStatsSummary(summary, parameters, playerStats) {
        for (let i = 0; i < parameters.length; i++) {
            const parameter = parameters[i];
            const targetParam = playerStats.parameters.find(p => p.parameter === parameter);
            if (targetParam) {
                summary[parameter].push({
                    scope: playerStats.scope,
                    parameters: [targetParam]
                });
            }
        }
    }
}

export default BoxScoreSummaryProvider;